import { useMemo, useState } from 'react';

import { Receipt } from 'lucide-react';

import { formatarData } from '@/nucleo/formato/datas';
import { Button } from '@/ui/base/button';
import { DataTable } from '@/ui/DataTable';
import { DateRangeFilter } from '@/ui/DateRangeFilter';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { PageHeader } from '@/ui/PageHeader';
import { StatusBadge, type StatusVariant } from '@/ui/StatusBadge';
import type { TableViewColumn } from '@/ui/table-view/tabelaVisoes';
import { useTableViewPreferences } from '@/ui/table-view/useTableViewPreferences';

import { GatingBanner } from '../comum/GatingBanner';
import { useCobrancas } from './dados';
import {
  ROTULO_DO_TIPO_DE_SERVICO,
  TAMANHO_PAGINA_PADRAO,
  type CobrancaAdministrativa,
  type FiltroDeCobrancas,
  type StatusDaCobranca,
  type TipoDeServicoCobrado,
} from './contrato';

/**
 * `/credenciado/cobrancas` — cobranças administrativas do credenciado.
 *
 * DAMSPs emitidos por serviços prestados a pedido do credenciado —
 * substituição de condutor, revisão de bancada, vistoria extraordinária e
 * afins —, com número, motivo, valor, vencimento e situação de pagamento.
 * Dado mockado — ver `dados.ts` e `comum/mock.ts` para a justificativa.
 *
 * Não existe fluxo de pagamento (PIX/boleto) aqui: o protótipo tampouco
 * tinha, então não é lacuna desta migração.
 */

/**
 * `StatusBadge` só aceita o conjunto fechado de selos de `@/ui/StatusBadge`.
 * Nenhum bate literalmente com a situação de uma cobrança, então este mapa
 * escolhe o selo de tom mais próximo — sucesso/atenção/problema/neutro — e
 * sempre passa o rótulo em português por fora, explícito.
 */
const SELO_DA_COBRANCA: Record<StatusDaCobranca, { variante: StatusVariant; rotulo: string }> = {
  pendente: { variante: 'pendente', rotulo: 'Pendente' },
  aguardando_pagamento: { variante: 'assinatura_pendente', rotulo: 'Aguardando pagamento' },
  paga: { variante: 'pago', rotulo: 'Paga' },
  vencida: { variante: 'bloqueado', rotulo: 'Vencida' },
  parcelada: { variante: 'processado', rotulo: 'Parcelada' },
  cancelada: { variante: 'cancelado', rotulo: 'Cancelada' },
};

const OPCOES_DE_STATUS = [
  { value: 'pendente', label: 'Pendente' },
  { value: 'aguardando_pagamento', label: 'Aguardando pagamento' },
  { value: 'paga', label: 'Paga' },
  { value: 'vencida', label: 'Vencida' },
  { value: 'parcelada', label: 'Parcelada' },
  { value: 'cancelada', label: 'Cancelada' },
];

const OPCOES_DE_TIPO_DE_SERVICO = (
  Object.entries(ROTULO_DO_TIPO_DE_SERVICO) as [TipoDeServicoCobrado, string][]
).map(([value, label]) => ({ value, label }));

function hojeEm(diasNoFuturo: number): string {
  const data = new Date();
  data.setDate(data.getDate() + diasNoFuturo);
  return data.toISOString().slice(0, 10);
}

/**
 * Módulo, e não dentro da tela: `useTableViewPreferences` pede lista estável.
 * O número identifica a linha e não se oculta; Tipo, Vencimento e Status
 * explicam os filtros de mesmo assunto.
 */
const COLUNAS: TableViewColumn<CobrancaAdministrativa>[] = [
  {
    id: 'numero',
    header: 'Número',
    cell: (cobranca) => <span className="font-mono text-sm">{cobranca.numero}</span>,
    sortKey: 'numero',
    isCritical: true,
  },
  {
    id: 'motivo',
    header: 'Motivo',
    cell: (cobranca) => <span className="text-sm">{cobranca.motivo}</span>,
  },
  {
    id: 'tipo',
    header: 'Tipo',
    cell: (cobranca) => (
      <span className="text-sm">{ROTULO_DO_TIPO_DE_SERVICO[cobranca.tipoServico]}</span>
    ),
    sortKey: (cobranca) => ROTULO_DO_TIPO_DE_SERVICO[cobranca.tipoServico],
    linkedFilterKeys: ['tipoServico'],
  },
  {
    id: 'valor',
    header: 'Valor',
    headerClassName: 'text-right',
    cellClassName: 'text-right',
    cell: (cobranca) => <MoneyDisplay value={cobranca.valor} />,
    sortKey: 'valor',
  },
  {
    id: 'vencimento',
    header: 'Vencimento',
    cell: (cobranca) => <span className="text-sm">{formatarData(cobranca.vencimento)}</span>,
    sortKey: 'vencimento',
    linkedFilterKeys: ['vencimento'],
  },
  {
    id: 'status',
    header: 'Status',
    cell: (cobranca) => {
      const selo = SELO_DA_COBRANCA[cobranca.status];
      return <StatusBadge status={selo.variante} label={selo.rotulo} />;
    },
    sortKey: 'status',
    isStatusColumn: true,
    linkedFilterKeys: ['status'],
  },
];

/** Destino do atalho "Ver cobranças" da faixa de CADIN: aqui ele não aparece. */
const CAMINHO_DAS_COBRANCAS = '/credenciado/cobrancas';

interface AtalhoDeFiltro {
  id: string;
  rotulo: string;
  aplicar: () => void;
}

export function CobrancasPage() {
  const [busca, setBusca] = useState('');
  const [tipoServico, setTipoServico] = useState('');
  const [status, setStatus] = useState('');
  const [vencimentoDe, setVencimentoDe] = useState('');
  const [vencimentoAte, setVencimentoAte] = useState('');
  const [pagina, setPagina] = useState(1);
  const [tamanho, setTamanho] = useState(TAMANHO_PAGINA_PADRAO);

  const limparFiltros = () => {
    setBusca('');
    setTipoServico('');
    setStatus('');
    setVencimentoDe('');
    setVencimentoAte('');
    setPagina(1);
  };

  const atalhos: AtalhoDeFiltro[] = [
    {
      id: 'vencendo-30-dias',
      rotulo: 'Vencendo em 30 dias',
      aplicar: () => {
        limparFiltros();
        setVencimentoDe(hojeEm(0));
        setVencimentoAte(hojeEm(30));
      },
    },
    {
      id: 'pendentes',
      rotulo: 'Pendentes',
      aplicar: () => {
        limparFiltros();
        setStatus('pendente');
      },
    },
    {
      id: 'pagas',
      rotulo: 'Pagas',
      aplicar: () => {
        limparFiltros();
        setStatus('paga');
      },
    },
  ];

  const filtro: FiltroDeCobrancas = {
    pagina,
    tamanho,
    busca: busca || undefined,
    tipoServico: (tipoServico || undefined) as TipoDeServicoCobrado | undefined,
    status: (status || undefined) as StatusDaCobranca | undefined,
    vencimentoDe: vencimentoDe || undefined,
    vencimentoAte: vencimentoAte || undefined,
  };
  const { data, isLoading, isError, refetch } = useCobrancas(filtro);

  const campos: FilterFieldConfig[] = [
    {
      key: 'busca',
      label: 'Busca',
      type: 'search',
      placeholder: 'Buscar por descrição ou nº DAMSP…',
      ariaLabel: 'Buscar cobranças',
      flex: true,
    },
    {
      key: 'tipoServico',
      label: 'Tipo de serviço',
      type: 'select',
      options: OPCOES_DE_TIPO_DE_SERVICO,
      width: 'w-full sm:w-[220px]',
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: OPCOES_DE_STATUS,
      width: 'w-full sm:w-[200px]',
    },
  ];

  const filterValues = useMemo(
    () => ({
      tipoServico,
      status,
      vencimento: vencimentoDe || vencimentoAte ? 'ativo' : '',
    }),
    [tipoServico, status, vencimentoDe, vencimentoAte],
  );

  const tableView = useTableViewPreferences({
    contextKey: 'credenciado:cobrancas:list',
    columns: COLUNAS,
    filterValues,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Cobranças"
        description="Cobranças administrativas vinculadas ao seu credenciamento."
      />

      <GatingBanner telaAtual={CAMINHO_DAS_COBRANCAS} />

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
          Atalhos
        </span>
        {atalhos.map((atalho) => (
          <Button key={atalho.id} variant="outline" size="sm" onClick={atalho.aplicar}>
            {atalho.rotulo}
          </Button>
        ))}
      </div>

      {isError ? (
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar as cobranças agora. Tente novamente em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : (
        <>
          <FilterBar
            fields={campos}
            values={{ busca, tipoServico, status }}
            onChange={(chave, valor) => {
              if (chave === 'busca') setBusca(valor);
              if (chave === 'tipoServico') setTipoServico(valor);
              if (chave === 'status') setStatus(valor);
              setPagina(1);
            }}
            onClear={limparFiltros}
          >
            <DateRangeFilter
              label="Vencimento"
              from={vencimentoDe}
              to={vencimentoAte}
              onChange={({ from, to }) => {
                setVencimentoDe(from);
                setVencimentoAte(to);
                setPagina(1);
              }}
            />
          </FilterBar>

          <DataTable
            columns={tableView.orderedColumns}
            personalizacao={tableView}
            data={data?.itens ?? []}
            isLoading={isLoading}
            total={data?.total}
            page={pagina}
            perPage={tamanho}
            onPageChange={setPagina}
            onPerPageChange={(novoTamanho) => {
              setTamanho(novoTamanho);
              setPagina(1);
            }}
            emptyIcon={Receipt}
            emptyTitle="Nenhuma cobrança encontrada"
            emptyDescription={
              busca || tipoServico || status || vencimentoDe || vencimentoAte
                ? 'Nenhuma cobrança está nesses filtros. Tente afrouxar a busca.'
                : 'Você não possui cobranças administrativas registradas.'
            }
            minWidth="900px"
            ariaLabel="Lista de cobranças administrativas"
          />
        </>
      )}
    </div>
  );
}

export default CobrancasPage;
